import { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const typeLabels = {
    offer: 'Offer',
    product: 'Product',
    alert: 'Alert',
};

const NotificationTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [triggeringId, setTriggeringId] = useState(null);

    const fetchNotifications = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/notifications');
            setNotifications(data.notifications);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load notifications", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteHandler = async (id, title) => {
        if (!window.confirm(`Delete notification "${title}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`/api/v1/admin/notification/${id}`);
            enqueueSnackbar("Notification deleted", { variant: "success" });
            setNotifications((prev) => prev.filter((n) => n._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete notification", { variant: "error" });
        }
    };

    const triggerHandler = async (id, title) => {
        if (!window.confirm(`Send "${title}" to customers right now?`)) return;
        setTriggeringId(id);
        try {
            const { data } = await axios.post(`/api/v1/admin/notification/${id}/trigger`);
            enqueueSnackbar(data.message || "Notification sent", { variant: "success" });
            fetchNotifications();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to send notification", { variant: "error" });
        } finally {
            setTriggeringId(null);
        }
    };

    const columns = [
        {
            field: "title",
            headerName: "Title",
            minWidth: 180,
            flex: 1,
        },
        {
            field: "type",
            headerName: "Type",
            minWidth: 100,
            flex: 0.4,
            renderCell: (params) => (
                <span className="bg-blue-50 text-primary-blue px-2 py-0.5 rounded-full text-xs font-medium">
                    {typeLabels[params.row.type] || params.row.type}
                </span>
            ),
        },
        {
            field: "body",
            headerName: "Message",
            minWidth: 220,
            flex: 1.2,
        },
        {
            field: "triggerCount",
            headerName: "Times Sent",
            minWidth: 100,
            flex: 0.4,
        },
        {
            field: "lastTriggeredAt",
            headerName: "Last Sent",
            minWidth: 140,
            flex: 0.6,
            renderCell: (params) => (
                params.row.lastTriggeredAt
                    ? <span className="text-sm text-gray-600">{new Date(params.row.lastTriggeredAt).toLocaleString()}</span>
                    : <span className="text-sm text-gray-400">Never</span>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 160,
            flex: 0.6,
            sortable: false,
            renderCell: (params) => (
                <div className="flex items-center gap-1">
                    <IconButton
                        color="success"
                        disabled={triggeringId === params.row.id}
                        onClick={() => triggerHandler(params.row.id, params.row.title)}
                        title="Send now"
                    >
                        <SendIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        component={Link}
                        to={`/admin/notification/${params.row.id}`}
                        title="Edit"
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        color="error"
                        onClick={() => deleteHandler(params.row.id, params.row.title)}
                        title="Delete"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </div>
            ),
        },
    ];

    const rows = notifications.map((n) => ({
        id: n._id,
        title: n.title,
        type: n.type,
        body: n.body,
        triggerCount: n.triggerCount || 0,
        lastTriggeredAt: n.lastTriggeredAt,
    }));

    return (
        <>
            <MetaData title="Admin: Notifications | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <h1 className="text-lg font-medium uppercase">Notifications</h1>
                <Link to="/admin/notification/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Notification</Link>
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full" style={{ height: 470 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={10}
                    disableSelectIconOnClick
                    sx={{ boxShadow: 0, border: 0 }}
                />
            </div>
        </>
    );
};

export default NotificationTable;
